import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CheckCircle2, Circle, Eye, MinusCircle, ScanText, Upload } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi, pedir } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { BotonArchivo } from "@/componentes/ui/BotonArchivo"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

type Documento = {
  id: number
  nombre_original: string
  mime: string
  creado_en: string
  leido_con_ia: boolean
}

type Item = {
  tipo: string
  seccion: string
  nombre: string
  ayuda: string
  lee_ia: boolean
  /** Quién lo entrega: el trabajador (lo ve en «Mi ficha») o la empresa. */
  entrega?: "TRABAJADOR" | "EMPRESA"
  estado: "CARGADO" | "NO_APLICA" | "PENDIENTE"
  documentos: Documento[]
  motivo: string | null
}

type Checklist = {
  secciones: Record<string, string>
  items: Item[]
  cargados: number
  aplican: number
}

/** Lo que el lector lee: PDF y fotos (`lectorPersonal.LEGIBLES`). */
const ACEPTA_IA = "application/pdf,image/png,image/jpeg,image/webp"
const ACEPTA = `${ACEPTA_IA},.doc,.docx`

/**
 * El checklist del EXPEDIENTE LABORAL (23/10, `services/personas_checklist.py`): como el
 * documental del expediente operativo del 2.0, pero para el trabajador.
 *
 * ⚠ **Cédula, RIF y currículo ALIMENTAN la ficha**: «Subir y leer con IA» los manda al
 * lector, que propone los datos en la ficha; al guardarla, el archivo entra al expediente
 * y la casilla queda cargada. Los demás se suben y ya.
 *
 * ⚠ **Informativo**: no bloquea contratos, nómina ni nada.
 */
export function ChecklistLaboral({
  personaId,
  onLeerConIa,
}: {
  personaId: number
  /** Manda el archivo al lector de la ficha. Sin él, todo se sube sin leer. */
  onLeerConIa?: (archivo: File) => void
}) {
  const cliente = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeEscribir = tienePermiso(usuario, "persona:administrar")
  const puedeLeer = puedeEscribir && tienePermiso(usuario, "persona:leer_ia") && onLeerConIa
  const [viendo, setViendo] = useState<ArchivoParaVer | null>(null)
  // «No aplica» pide su motivo en línea, en la misma fila.
  const [omitiendo, setOmitiendo] = useState<string | null>(null)
  const [motivo, setMotivo] = useState("")
  const clave = ["persona-checklist", personaId]

  const consulta = useQuery({
    queryKey: clave,
    queryFn: () => api.get<Checklist>(`/api/v1/personas/${personaId}/checklist`),
  })
  const refrescar = () => {
    cliente.invalidateQueries({ queryKey: clave })
    cliente.invalidateQueries({ queryKey: ["persona-documentos", personaId] })
  }
  const subir = useMutation({
    mutationFn: ({ tipo, archivo }: { tipo: string; archivo: File }) => {
      const datos = new FormData()
      datos.append("tipo", tipo)
      datos.append("archivo", archivo)
      return pedir(`/api/v1/personas/${personaId}/documentos`, { method: "POST", body: datos })
    },
    onSuccess: refrescar,
  })
  const noAplica = useMutation({
    mutationFn: ({ tipo, motivo }: { tipo: string; motivo: string | null }) =>
      api.put(`/api/v1/personas/${personaId}/checklist/${tipo}/no-aplica`, { motivo }),
    onSuccess: () => {
      setOmitiendo(null)
      setMotivo("")
      refrescar()
    },
  })
  const pendiente = useMutation({
    mutationFn: (tipo: string) =>
      pedir(`/api/v1/personas/${personaId}/checklist/${tipo}/no-aplica`, { method: "DELETE" }),
    onSuccess: refrescar,
  })

  if (consulta.isPending) return <EstadoCarga etiqueta="Cargando el checklist…" filas={3} />
  if (consulta.isError) {
    return (
      <EstadoError
        titulo="No se pudo cargar el checklist"
        error={consulta.error}
        onReintentar={() => consulta.refetch()}
      />
    )
  }
  const c = consulta.data
  const error = subir.error ?? noAplica.error ?? pendiente.error

  return (
    <section aria-label="Checklist del expediente laboral" className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Expediente laboral</h2>
        <span className="text-sm tabular-nums" style={{ color: "var(--fg-muted)" }}>
          {c.cargados} de {c.aplican} documentos
        </span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full"
        style={{ background: "var(--border)" }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={c.aplican}
        aria-valuenow={c.cargados}
        aria-label="Documentos cargados"
      >
        <div
          className="h-full rounded-full"
          style={{
            width: `${c.aplican === 0 ? 100 : Math.round((c.cargados / c.aplican) * 100)}%`,
            background: "var(--success)",
          }}
        />
      </div>
      {error && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {error instanceof ErrorApi ? error.message : "No se pudo guardar"}
        </p>
      )}

      {Object.entries(c.secciones).map(([seccion, titulo]) => (
        <div key={seccion} className="space-y-1">
          <h3 className="text-xs font-semibold uppercase" style={{ color: "var(--fg-muted)" }}>
            {titulo}
          </h3>
          <ul className="tarjeta divide-y" style={{ borderColor: "var(--border)" }}>
            {c.items
              .filter((i) => i.seccion === seccion)
              .map((i) => (
                <li key={i.tipo} className="flex flex-wrap items-start gap-3 p-3 text-sm">
                  {i.estado === "CARGADO" ? (
                    <CheckCircle2
                      size={18}
                      aria-label="Cargado"
                      style={{ color: "var(--success)" }}
                    />
                  ) : i.estado === "NO_APLICA" ? (
                    <MinusCircle
                      size={18}
                      aria-label="No aplica"
                      style={{ color: "var(--fg-muted)" }}
                    />
                  ) : (
                    <Circle size={18} aria-label="Pendiente" style={{ color: "var(--warning)" }} />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">
                      {i.nombre}
                      {i.entrega === "EMPRESA" && (
                        <span
                          className="ml-2 text-xs font-normal"
                          style={{ color: "var(--fg-muted)" }}
                        >
                          · lo tramita la empresa
                        </span>
                      )}
                    </p>
                    <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                      {i.estado === "NO_APLICA"
                        ? `No aplica${i.motivo ? `: ${i.motivo}` : ""}`
                        : i.ayuda}
                    </p>
                    {i.documentos.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        className="mt-1 flex items-center gap-1 text-xs underline-offset-2 hover:underline"
                        onClick={() =>
                          setViendo({
                            ruta: `/api/v1/personas/${personaId}/documentos/${d.id}/archivo`,
                            nombre: d.nombre_original,
                          })
                        }
                      >
                        <Eye size={12} aria-hidden={true} />
                        {d.nombre_original} · {comoFecha(d.creado_en)}
                        {d.leido_con_ia ? " · leído con IA" : ""}
                      </button>
                    ))}
                  </div>
                  {puedeEscribir && i.tipo !== "FOTO" && (
                    <div className="flex flex-wrap items-center gap-2">
                      {i.lee_ia && puedeLeer ? (
                        <BotonArchivo
                          accept={ACEPTA_IA}
                          etiqueta={`Subir y leer con IA ${i.nombre}`}
                          onArchivo={(f) => onLeerConIa?.(f)}
                        >
                          <ScanText size={14} aria-hidden={true} />
                          Subir y leer con IA
                        </BotonArchivo>
                      ) : (
                        <BotonArchivo
                          accept={ACEPTA}
                          etiqueta={`Subir ${i.nombre}`}
                          disabled={subir.isPending}
                          onArchivo={(f) => subir.mutate({ tipo: i.tipo, archivo: f })}
                        >
                          <Upload size={14} aria-hidden={true} />
                          {i.estado === "CARGADO" ? "Añadir otro" : "Subir"}
                        </BotonArchivo>
                      )}
                      {i.estado === "PENDIENTE" && omitiendo !== i.tipo && (
                        <Boton tamano="sm" variante="fantasma" onClick={() => setOmitiendo(i.tipo)}>
                          No aplica
                        </Boton>
                      )}
                      {i.estado === "NO_APLICA" && (
                        <Boton
                          tamano="sm"
                          variante="fantasma"
                          onClick={() => pendiente.mutate(i.tipo)}
                        >
                          Volver a pendiente
                        </Boton>
                      )}
                    </div>
                  )}
                  {omitiendo === i.tipo && (
                    <div className="flex w-full flex-wrap items-center gap-2">
                      <input
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                        maxLength={200}
                        placeholder="Por qué no aplica (opcional): no tiene hijos, primer empleo…"
                        aria-label={`Por qué no aplica ${i.nombre}`}
                        className="campo min-w-0 flex-1"
                      />
                      <Boton
                        tamano="sm"
                        variante="primario"
                        disabled={noAplica.isPending}
                        onClick={() =>
                          noAplica.mutate({ tipo: i.tipo, motivo: motivo.trim() || null })
                        }
                      >
                        Marcar
                      </Boton>
                      <Boton tamano="sm" variante="fantasma" onClick={() => setOmitiendo(null)}>
                        Cancelar
                      </Boton>
                    </div>
                  )}
                </li>
              ))}
          </ul>
        </div>
      ))}
      <VistaPreviaArchivo archivo={viendo} onCerrar={() => setViendo(null)} />
    </section>
  )
}
